import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "I need a booking site for my clinic",
  "Can you build an AI chatbot for support?",
  "How would you approach automating our sales handoff?",
  "What CRM would you recommend for a 10-person team?",
];

export default function ChatPage({ projectIdOverride = null }) {
  const [searchParams] = useSearchParams();
  const projectId = projectIdOverride || searchParams.get("project_id");
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I'm the Agenaix Assistant — here to help you scope your AI automation project. Tell me what you're trying to build and I'll walk you through the right services." },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const [project, setProject] = useState(null);
  const [contextLoading, setContextLoading] = useState(Boolean(projectId));
  const [contextError, setContextError] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!projectId) {
      setProject(null);
      setContextLoading(false);
      setContextError("");
      return () => { cancelled = true; };
    }
    setContextLoading(true);
    setContextError("");
    api.get(`/projects/${encodeURIComponent(projectId)}`)
      .then(({ data }) => { if (!cancelled) setProject(data); })
      .catch(() => { if (!cancelled) setContextError("Project context could not be loaded."); })
      .finally(() => { if (!cancelled) setContextLoading(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending || contextLoading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const contextMessage = project && !sessionId
        ? [
            "Project context:",
            `ID: ${project.id}`,
            `Title: ${project.title || "Untitled"}`,
            `Status: ${project.status || "unknown"}`,
            `Service: ${project.service_type || "unknown"}`,
            `Description: ${project.description || "Not provided"}`,
            `Budget: ${project.budget || "Not provided"}`,
            `Timeline: ${project.timeline || "Not provided"}`,
            "",
            `User message: ${msg}`,
          ].join("\n")
        : msg;
      const { data } = await api.post("/chat", { message: contextMessage, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Agenaix Assistant is unavailable");
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I hit an error. Please try again in a moment." }]);
    } finally { setSending(false); }
  };

  return (
    <AppShell subtitle="◇ AGENAIX ASSISTANT · CLAUDE SONNET 4.5" title="AI project consultant">
      <div className="max-w-3xl mx-auto">
        {project && (
          <div className="mb-4 border border-[#0B4DCC]/25 bg-[#EEF4FF] rounded-md px-4 py-3" data-testid="chat-project-context">
            <div className="fx-mono-tag text-[#0B4DCC]">DISCUSSING PROJECT</div>
            <div className="mt-1 font-semibold">{project.title}</div>
            <div className="mt-0.5 text-xs text-neutral-600 font-mono">{project.id.slice(0, 8)} · {project.status}</div>
          </div>
        )}
        {contextLoading && (
          <div className="mb-4 text-sm text-neutral-500" data-testid="chat-context-loading">Loading project context…</div>
        )}
        {contextError && (
          <div className="mb-4 border border-red-200 bg-red-50 text-red-700 rounded-md px-4 py-3 text-sm" data-testid="chat-context-error">
            {contextError} You can continue with the general assistant.
          </div>
        )}
        <div ref={scrollRef} className="border border-black/10 rounded-md p-6 overflow-y-auto space-y-5 h-[calc(100vh-320px)] min-h-[380px]" data-testid="chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}-${i}`}>
              <div className={`max-w-[85%] px-5 py-3 rounded-md text-sm leading-relaxed ${
                m.role === "user" ? "bg-[#0B1F3A] text-white" : "bg-[#F4F6F8] text-[#0B1F3A] border border-black/5"
              }`}>
                {m.role === "assistant" && (
                  <div className="flex items-center gap-1.5 mb-1.5 text-xs font-mono uppercase tracking-widest text-[#0B4DCC]">
                    <Sparkle size={12} weight="fill" /> AGENAIX
                  </div>
                )}
                <div className="whitespace-pre-wrap">{m.content}</div>
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-[#F4F6F8] text-neutral-500 px-5 py-3 rounded-md text-sm border border-black/5">
                <div className="flex items-center gap-2"><Sparkle size={12} weight="fill" className="text-[#0B4DCC] animate-pulse" /> Agenaix Assistant is thinking…</div>
              </div>
            </div>
          )}
        </div>

        {messages.length <= 1 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s, i) => (
              <button key={i} onClick={() => send(s)} className="text-xs font-mono uppercase tracking-widest px-3 py-2 border border-black/15 hover:bg-black hover:text-white transition-colors rounded-md" data-testid={`chat-suggestion-${i}`}>
                {s}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-2" data-testid="chat-form">
          <Input value={input} onChange={(e) => setInput(e.target.value)} disabled={contextLoading} placeholder={project ? `Ask about ${project.title}…` : "Ask Agenaix Assistant anything…"} className="h-12 rounded-md flex-1" data-testid="chat-input" />
          <Button type="submit" disabled={sending || contextLoading || !input.trim()} className="fx-btn-primary h-12 px-5 rounded-md" data-testid="chat-send-btn">
            <PaperPlaneTilt size={16} weight="bold" />
          </Button>
        </form>
      </div>
    </AppShell>
  );
}
